import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { InvestorCommunicationStatus } from "@entrilia/shared/api/investorCommunicationsTypes";
import { allInvestorCommunicationStatuses } from "@entrilia/shared/api/investorCommunicationsTypes";
import { logError } from "@entrilia/shared/logging";
import { numberComparerBy } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../api/adminApi";
import type {
  InvestorCommunicationInfo,
  InvestorCommunicationType,
  SearchInvestorCommunicationsRequest,
} from "../../../api/types/investorCommunicationsTypes";
import { allInvestorCommunicationTypes } from "../../../api/types/investorCommunicationsTypes";
import type { FilterState } from "../../common/filters/filterState";
import type {
  DateFilter,
  MultiSelectFilter,
  SelectOption,
  TableFilter,
  TableFilterDefinition,
} from "../../common/filters/filterTypes";

export const filterDefinitions: TableFilterDefinition<InvestorCommunicationInfo>[] = [
  {
    id: "type",
    name: "Type",
    type: "multi_select",
    operatorOptions: ["one_of"],
    predefinedOptions: allInvestorCommunicationTypes.map((type) => ({ value: type, label: type })),
  },
  {
    id: "status",
    name: "Status",
    type: "multi_select",
    operatorOptions: ["one_of"],
    predefinedOptions: allInvestorCommunicationStatuses.map((status) => ({ value: status, label: status })),
  },
  {
    id: "fundIds",
    name: "Fund",
    type: "multi_select",
    operatorOptions: ["one_of"],
    asyncOptions: true,
  },
  {
    id: "categoryIds",
    name: "Category",
    type: "multi_select",
    operatorOptions: ["one_of"],
    asyncOptions: true,
  },
  {
    id: "publishedAt",
    name: "Published",
    type: "date",
    operatorOptions: ["range"],
  },
];

const getFunds = withErrorHandling(() => adminApi.getObjectsAsLookupReferences("Fund"));

const getMessageAccessCategories = withErrorHandling(() => adminApi.getAccessCategoriesOfTypes(["Message"]));

const getFundOptions = async (): Promise<SelectOption[]> => {
  const [funds, error] = await getFunds();
  if (error) {
    logError(error, "[investorCommunicationsGridFilters] getObjectsAsLookupReferences");
    return [];
  }

  return funds.map((fund) => ({ value: fund.objectId, label: fund.primaryFieldValue }));
};

const getCategoryOptions = async (): Promise<SelectOption[]> => {
  const [categories, error] = await getMessageAccessCategories();
  if (error) {
    logError(error, "[investorCommunicationsGridFilters] getAccessCategoriesOfTypes");
    return [];
  }

  return categories.sort(numberComparerBy((c) => c.sortOrder)).map((c) => ({ value: c.externalId, label: c.name }));
};

export const selectOptionsResolver = async (filterId: string): Promise<SelectOption[]> => {
  switch (filterId) {
    case "fundIds":
      return getFundOptions();
    case "categoryIds":
      return getCategoryOptions();
    default: {
      logError(`Unknown async select filterId: ${filterId}`, "[investorCommunicationsGridFilters]");
      return [];
    }
  }
};

const getMultiSelectFilterValues = (filterId: string, visibleFilters: TableFilter<InvestorCommunicationInfo>[]) => {
  const filter = visibleFilters.find((f) => f.id === filterId) as
    MultiSelectFilter<InvestorCommunicationInfo> | undefined;
  const selectedValues = filter?.value?.operator === "one_of" ? filter.value.selectedValues : undefined;
  return selectedValues?.length ? selectedValues : undefined;
};

const getDateRangeFilterValues = (filterId: string, visibleFilters: TableFilter<InvestorCommunicationInfo>[]) => {
  const filter = visibleFilters.find((f) => f.id === filterId) as DateFilter<InvestorCommunicationInfo> | undefined;
  if (filter?.value?.operator !== "range") {
    return [undefined, undefined];
  }

  const { range } = filter.value;
  return [range?.[0] || undefined, range?.[1] || undefined];
};

export const getInvestorCommunicationsFilterPayload = (
  filterState: FilterState<InvestorCommunicationInfo>
): Omit<SearchInvestorCommunicationsRequest, "range"> => {
  const [publishedAtFrom, publishedAtTo] = getDateRangeFilterValues("publishedAt", filterState.visibleFilters);

  return {
    searchTerm: filterState.search.value || undefined,
    types: getMultiSelectFilterValues("type", filterState.visibleFilters) as InvestorCommunicationType[] | undefined,
    statuses: getMultiSelectFilterValues("status", filterState.visibleFilters) as
      InvestorCommunicationStatus[] | undefined,
    fundIds: getMultiSelectFilterValues("fundIds", filterState.visibleFilters),
    categoryIds: getMultiSelectFilterValues("categoryIds", filterState.visibleFilters),
    publishedAtFrom,
    publishedAtTo,
  };
};
